import { Show, For, createMemo, createSignal, createEffect, onCleanup } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import { gold, qty, medal } from '../lib/format.js';
import { pickUser } from '../lib/picks.js';
import { pickSettingOr } from '../lib/settingsLayers.js';
import TradeWeekPicker from '../components/TradeWeekPicker.jsx';
import GuildSearchInput from '../components/GuildSearchInput.jsx';
import { EMOJI } from '../lib/emoji.js';
import { tipProps } from '../components/MouseTooltip.jsx';

// Sprint-17 #167 — chip-style guild filter (mirrors GuildTracking pattern).
const GUILD_KEYS  = ['all', 'post', 'caravan', 'dealers'];
const GUILD_LABEL = {
  all:     'All',
  post:    `${EMOJI.post} Post`,
  caravan: `${EMOJI.caravan} Caravan`,
  dealers: `${EMOJI.dealers} Dealers`,
};
const GUILD_SHORT = { all: 'ALL', post: 'POST', caravan: 'CARAVAN', dealers: 'DEALERS' };
const METRIC_SHORT = { gold: 'GOLD', sales: 'SALES' };

export default function Leaderboard(props) {
  let rootRef;
  const limit = () => pickSettingOr(props.settings, 'limit', !!props.maximized, 25);
  const initialOffset = () => {
    const v = props.settings?.weekOffset;
    if (v === 'all' || v === -1 || v === '-1') return -1;
    return Math.max(0, Number(v) || 0);
  };

  const [range, setRange] = createSignal(
    props.settings?.startTs != null && props.settings?.endTs != null
      ? { startTs: Number(props.settings.startTs), endTs: Number(props.settings.endTs) }
      : { weekOffset: initialOffset() },
  );
  const [guild, setGuild] = createSignal(props.settings?.guild || 'all');
  // Sprint-17 #167 — admin non-Redfur guild lookup (null = use chip filter).
  const [customGuild, setCustomGuild] = createSignal(
    props.settings?.guildIndex
      ? { index: Number(props.settings.guildIndex), name: props.settings.guildName || `Guild #${props.settings.guildIndex}` }
      : null,
  );

  createEffect(() => {
    setGuild(props.settings?.guild || 'all');
  });

  const isAdmin = () => !!wm.state.identity?.isAdmin;
  const metric = () => props.settings?.metric || 'gold';

  const { data, loading, error } = useWidgetData('leaderboard',
    () => {
      const r = range();
      const cg = customGuild();
      const base = cg
        ? { limit: limit(), guildIndex: cg.index, metric: metric() }
        : { limit: limit(), guild: guild(), metric: metric() };
      if (r.startTs != null && r.endTs != null) {
        return { ...base, startTs: r.startTs, endTs: r.endTs };
      }
      return { ...base, weekOffset: r.weekOffset };
    });

  const weekOffset = () => {
    const r = range();
    return (r.startTs != null && r.endTs != null) ? null : r.weekOffset;
  };

  const rows = createMemo(() => {
    const list = (data()?.leaderboard) || [];
    if (metric() === 'sales') {
      const sorted = list.slice().sort((a, b) => (Number(b.totalSales) || 0) - (Number(a.totalSales) || 0));
      return sorted.map((r, i) => ({ ...r, rank: i + 1 }));
    }
    return list;
  });

  const meTag = () => (props.viewerTag || '').toLowerCase();
  // Single pass over rows() for both totals instead of two separate reduces.
  const totals = createMemo(() => {
    let g = 0, s = 0;
    for (const r of rows()) { g += Number(r.totalGold) || 0; s += Number(r.totalSales) || 0; }
    return { gold: g, sales: s };
  });
  const totalGold = () => totals().gold;
  const totalSales = () => totals().sales;
  const weekLabel = () => data()?.weekLabel || (weekOffset() === -1 ? 'All time' : 'This week');

  // Multi-guild placement tooltip. The backend already privacy-filters and
  // role-filters these arrays; keep the frontend as a renderer only.
  function guildTooltip(r) {
    const redfur = r.guilds || [];
    const others = r.otherGuilds || [];
    if (!redfur.length && !others.length) return '';
    const lines = [];
    if (r.membershipSummary?.label) lines.push(r.membershipSummary.label);
    if (redfur.length) {
      lines.push('Redfur sales');
      for (const g of redfur) {
        const rank = g.rank ? `#${g.rank} · ` : '';
        lines.push(`${g.guildName}: ${rank}${gold(g.totalGold)} · ${qty(g.totalSales)} sales${g.totalVolume ? ` · ${qty(g.totalVolume)} items` : ''}`);
      }
    }
    if (others.length) {
      lines.push('Other guilds (admin)');
      for (const g of others) {
        lines.push(`${g.guildName}: ${gold(g.totalGold)} · ${qty(g.totalSales)} sales${g.totalVolume ? ` · ${qty(g.totalVolume)} items` : ''}`);
      }
    }
    return lines.join('\n');
  }

  function membershipLabel(r) {
    const summary = r.membershipSummary;
    if (!summary || !summary.label) return '';
    return summary.totalCount > 1 || summary.allRedfur ? summary.label : '';
  }

  function persistPatch(patch) {
    if (!props.widget?.id) return;
    try { wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), ...patch }); } catch {}
  }

  function updateGuild(next) {
    const value = next || 'all';
    setGuild(value);
    setCustomGuild(null);
    persistPatch({ guild: value, guildIndex: null, guildName: null });
  }

  function updateRange(next) {
    setRange(next);
    if (next?.startTs != null && next?.endTs != null) {
      persistPatch({ startTs: next.startTs, endTs: next.endTs, weekOffset: null });
    } else {
      persistPatch({ weekOffset: next?.weekOffset ?? 0, startTs: null, endTs: null });
    }
  }

  function updateCustomGuild(g) {
    setCustomGuild(g || null);
    persistPatch({
      guildIndex: g?.index || null,
      guildName: g?.name || null,
    });
  }

  function activateControl(selector, click = false) {
    const el = rootRef?.querySelector(selector);
    if (!el) return;
    el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    if (click) el.click?.();
    el.focus?.({ preventScroll: true });
  }

  function openWidgetSettings() {
    if (!props.widget?.id || typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('v2-open-window-settings', { detail: { widgetId: props.widget.id } }));
  }

  const weekChromeValue = () => {
    const r = range();
    if (r.startTs != null && r.endTs != null) return 'RANGE';
    if (weekOffset() === -1) return 'ALL TIME';
    if (weekOffset() === 0) return 'THIS WK';
    return `-${weekOffset()}W`;
  };
  const guildChromeValue = () => customGuild()?.name || GUILD_SHORT[guild()] || 'ALL';
  const guildChromeTitle = () => customGuild()?.name
    ? `Guild filter: ${customGuild().name}`
    : `Guild filter: ${GUILD_LABEL[guild()] || 'All'}`;

  createEffect(() => {
    props.setTitleChrome?.({
      filters: [
        {
          key: 'guild',
          label: 'Guild',
          value: guildChromeValue(),
          title: `${guildChromeTitle()} — click to edit`,
          onActivate: () => activateControl(customGuild() ? '.v2-gt-search input' : '.v2-gt-filters .is-on'),
        },
        {
          key: 'week',
          label: 'Week',
          value: weekChromeValue(),
          title: `Trade week: ${weekLabel()} — click to edit`,
          onActivate: () => activateControl('.v2-twp__center, .v2-twp__center--mini, .v2-twp__inline-num', true),
        },
        {
          key: 'rank',
          label: 'Rank',
          value: METRIC_SHORT[metric()] || 'GOLD',
          title: 'Rank metric — click to open widget settings',
          onActivate: openWidgetSettings,
        },
      ],
    });
  });
  onCleanup(() => props.setTitleChrome?.(null));

  // Show "+N more" badge when a seller appears in multiple guild leaderboards
  // and the current view is "all guilds" (not filtered to a single guild).
  const showMultiGuild = () => guild() === 'all' && !customGuild();

  return (
    <div class="v2-widget" ref={el => rootRef = el}>
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__sub">{weekLabel()}</span>
      </div>
      {/* Sprint-17 #167 — chip-style guild filter, mirroring GuildTracking. */}
      <div class="v2-widget__toolbar">
        <div class="v2-gt-filters">
          <Show when={!customGuild()} fallback={
            <>
              <span class="v2-gt-chip is-on">{customGuild()?.name}</span>
              <button class="v2-gt-chip" onClick={() => updateCustomGuild(null)}>&#x2715; Clear</button>
            </>
          }>
            <For each={GUILD_KEYS}>{(k) => (
              <button
                class={`v2-gt-chip ${guild() === k ? 'is-on' : ''}`}
                onClick={() => updateGuild(k)}
              >{GUILD_LABEL[k]}</button>
            )}</For>
          </Show>
        </div>
        <span class="grow" />
        <TradeWeekPicker
          value={range()}
          onChange={updateRange}
          maxWeeksAgo={51}
          allowAllTime
          allowCustom
        />
      </div>
      {/* Sprint-17 #167 — admin-only non-Redfur guild autosuggest. */}
      <Show when={isAdmin()}>
        <div class="v2-gt-search">
          <GuildSearchInput
            placeholder="Look up any guild…"
            initialValue={customGuild()?.name || ''}
            onSelect={(g) => updateCustomGuild(g)}
            onClear={() => updateCustomGuild(null)}
          />
        </div>
      </Show>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-stat-grid">
            <div class="v2-stat v2-stat--gold">
              <span class="v2-stat__label">Top {rows().length} gold</span>
              <span class="v2-stat__value">{gold(totalGold())}</span>
            </div>
            <div class="v2-stat v2-stat--cyan">
              <span class="v2-stat__label">Sales tracked</span>
              <span class="v2-stat__value">{qty(totalSales())}</span>
            </div>
          </div>
          <Show when={rows().length} fallback={<div class="v2-empty">No leaderboard snapshot yet.</div>}>
            <div class="v2-widget__scroll">
              <ol class="v2-rank-list">
                <For each={rows()}>{(r) => {
                  const isMe = meTag() && String(r.sellerName || '').toLowerCase() === meTag();
                  const tip = guildTooltip(r);
                  const memberLabel = membershipLabel(r);
                  return (
                    <li class={`v2-rank-row${isMe ? ' is-me' : ''}${r._hiddenFromPublic ? ' is-private' : ''}`} {...(tip ? tipProps({ content: tip, pin: false }) : {})}>
                      <span class="v2-rank-row__medal">{medal(r.rank)}</span>
                      <span style={{ 'min-width': 0 }}>
                        <span class="v2-rank-row__name v2-pick v2-pick--user" onClick={() => pickUser(r.sellerName)}>{r.sellerName || '—'}</span>
                        <Show when={showMultiGuild() && memberLabel}>
                          <span class="v2-lb-membership">{memberLabel}</span>
                        </Show>
                        <Show when={r._hiddenFromPublic}>
                          <span class="v2-lb-private">private</span>
                        </Show>
                        <span class="v2-rank-row__sub">
                          {qty(r.totalSales || 0)} sales{r.totalVolume ? ` · ${qty(r.totalVolume)} items` : ''}
                        </span>
                      </span>
                      <span class="v2-rank-row__value">{gold(r.totalGold)}</span>
                    </li>
                  );
                }}</For>
              </ol>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
